<template lang="pug">
SideOverlaySlot(
  :isOpen="isOpen",
  title="REFERRAL DETAILS",
  @closeOverlay="closeOverlay"
)
  template(#contentTop)
    section.patient-section
      .patient-section__top
        p.bold.patient-name {{ showReferral?.patient?.first }} {{ showReferral?.patient?.last }}
        p.small-text
          span Date/Time: {{ getDateFromUnixFormatted(showReferral?.scheduledAt) }} {{ showReferral?.timeOfAppointment || '' }}
        p.small-text
          span Referral Type: {{ getReferralType(showReferral) }}
        p.bold
          span Provider/Location: {{ showReferral?.providerLocation || 'N/A' }}

    .follow-up-warning(v-if="isSecondFollowUpDue")
      .follow-up-warning__content
        span Final Follow Up Due!
    .follow-up-warning(v-else-if="isFirstFollowUpDue")
      .follow-up-warning__content
        span 1st Follow Up Due!

  template(#scrollbox)
    .overlay-pairs.history-section
      .item(v-for="entry in sortedEntries" :key="entry.id || 'initial'")
        .date-user
          .date-user__line
          .date-user__txt {{ getDateFromUnixFormatted(entry.createdAt) }} {{ getTimeFromUnixFormatted(entry.createdAt) }} by {{ entry.communityName || showReferral?.communityCare?.name || 'Community Name' }}
        
        // Initial referral entry
        template(v-if="entry.isInitial")
          .pairs.columns
            .pair.column.is-half
              .pair-label Note Type
              .pair-txt <strong>{{ formatReferralAttempt(entry.referralAttempt) }}</strong>
          .pairs.columns
            .pair.column
              .pair-label Insurance Options
              .pair-txt {{ entry.insuranceOptions || 'N/A' }}
          .pairs.columns
            .pair.column
              .pair-label Notes
              .pair-txt
                p {{ entry.notes || 'No notes available' }}
        
        // Follow-up entries
        template(v-else-if="entry.action=='complete_referral'")
          .pairs.columns
            .pair.column.is-half
              .pair-label Referral Status
              .pair-txt <strong>Completed</strong>
            .pair.column.is-half
              .pair-label Able to Reach
              .pair-txt <strong>{{ formatYesNoNA(entry.ableToReach) }}</strong>
          .pairs.columns
            .pair.column.is-half
              .pair-label Went to Appointment
              .pair-txt <strong>{{ formatYesNoNA(entry.wentToAppointment) }}</strong>
            .pair.column.is-half(v-if="entry.scheduledAt")
              .pair-label Appointment Information
              .pair-txt {{ getDateFromUnixFormatted(entry.scheduledAt) }} {{ showReferral?.timeOfAppointment || '' }}
          .pairs.columns(v-if="entry.notes")
            .pair.column
              .pair-label Notes
              .pair-txt
                p {{ entry.notes }}
        template(v-else)
          .pairs.columns
            .pair.column.is-half
              .pair-label Follow-up Type
              .pair-txt <strong>{{ getFollowUpTypeFormatted(entry.followUpType) }}</strong>
            .pair.column.is-half
              .pair-label Able to Reach
              .pair-txt <strong>{{ formatYesNoNA(entry.ableToReach) }}</strong>
          .pairs.columns
            .pair.column.is-half
              .pair-label Went to Appointment
              .pair-txt <strong>{{ formatYesNoNA(entry.wentToAppointment) }}</strong>
            .pair.column.is-half(v-if="entry.scheduledAt")
              .pair-label Appointment Information
              .pair-txt {{ getDateFromUnixFormatted(entry.scheduledAt) }} {{ showReferral?.timeOfAppointment || '' }}
          .pairs.columns(v-if="entry.notes")
            .pair.column
              .pair-label Notes
              .pair-txt
                p {{ entry.notes }}

  template(#footer)
    .flex.flex--space-between
      BaseBtn.x-lg.red-outline(
        href="#",
        @click.prevent="closeOverlay"
      ) CLOSE
      BaseBtn.x-lg.green(
        v-if="showFollowUpButton"
        href="#",
        :class="{disabled: isReferralCompleted}"
        @click.prevent="addFollowUp"
      ) ADD FOLLOW-UP

AddFollowUpOverlay(
  :isOpen="isAddFollowUpOverlayOpen",
  :showReferral="showReferral",
  @closeAddFollowUpOverlay="closeAddFollowUpOverlay",
  @followUpSaved="handleFollowUpSaved"
)

</template>

<script>
import { defineComponent } from "vue";
import SideOverlaySlot from "@/components/overlays/SideOverlaySlot.vue";
import AddFollowUpOverlay from "./AddFollowUpOverlay.vue";

import {
  getDateFromUnixFormatted,
  getTimeFromUnixFormatted,
  getDobFormattedforDateField,
  getPhoneFormatted,
} from "@/helpers";

import { PATIENT_NAVIGATION_STAGE_OPTIONS } from "@/helpers/constants";

import {
  fetchPatientNavigationReferralById,
  createPatientNavigationReferralFollowUp,
  fetchPatientNavigationReferralFollowUps,
} from "@/api/patientNavigation";
import { fetchPatientNavigationSettings } from "@/api/patientNavigationSettingsApi";
import { fetchCommunityCareOrganizationById } from "@/api/communityOrganization";
import { apiStatus } from "@/api";
const { IDLE, PENDING, SUCCESS, ERROR } = apiStatus;

export default defineComponent({
  components: {
    SideOverlaySlot,
    AddFollowUpOverlay,
  },
  emits: ["closeReferralRecordOverlay", "refreshPatientReferrals"],

  props: {
    isOpen: {
      type: Boolean,
      default: false,
    },
    showReferral: {
      type: Object,
    },
    fromNotifications: {
      type: Boolean,
      default: false,
    },
    showFollowUpButton: {
      type: Boolean,
      default: true,
    },
  },

  data() {
    return {
      getDateFromUnixFormatted: getDateFromUnixFormatted,
      getTimeFromUnixFormatted: getTimeFromUnixFormatted,
      getDobFormattedforDateField: getDobFormattedforDateField,
      getPhoneFormatted: getPhoneFormatted,

      fetchPatientReferralByIdStatus: IDLE,
      showReferralDetails: null,
      isAddFollowUpOverlayOpen: false,
      referralTypeMap: {},
      communityOrganization: null,
      followUps: [],
    };
  },

  computed: {
    isFirstFollowUpDue() {
      return (
        !this.isReferralCompleted &&
        (this.showReferral?.isFirstFollowUpDue || false)
      );
    },

    isSecondFollowUpDue() {
      return (
        !this.isReferralCompleted &&
        (this.showReferral?.isSecondFollowUpDue || false)
      );
    },

    isReferralCompleted() {
      return this.showReferral?.status === "Completed";
    },

    sortedEntries() {
      const initialEntry = {
        id: "initial",
        isInitial: true,
        createdAt: this.showReferral?.createdAt,
        communityName:
          this.showReferral?.communityCare?.name || "Community Name",
        noteType: this.showReferral?.noteType,
        insuranceOptions: this.showReferral?.insuranceOptions,
        notes: this.showReferralDetails?.notes,
        referralAttempt: this.showReferral?.referralAttempt,
      };

      const allEntries = [...this.followUps, initialEntry];

      return allEntries.sort((a, b) => {
        const aTime = a.createdAt || 0;
        const bTime = b.createdAt || 0;
        return bTime - aTime; // Most recent first
      });
    },
  },

  methods: {
    formatReferralAttempt(referralAttempt) {
      const attemptMap = {
        "new-referral": "New Referral",
        "first-reschedule": "1st Reschedule",
        "second-reschedule": "2nd Reschedule",
      };
      return attemptMap[referralAttempt] || referralAttempt || "New Referral";
    },

    closeOverlay() {
      this.$emit("closeReferralRecordOverlay");
    },

    addFollowUp() {
      if (this.isReferralCompleted) {
        return;
      }
      this.isAddFollowUpOverlayOpen = true;
    },

    closeAddFollowUpOverlay() {
      this.isAddFollowUpOverlayOpen = false;
      this.closeOverlay();
    },

    async handleFollowUpSaved(followUpData) {
      try {
        await createPatientNavigationReferralFollowUp(
          followUpData.idPatientReferral,
          {
            action: followUpData.action,
            followUpType: followUpData.followUpType,
            ableToReach: followUpData.ableToReach,
            wentToAppointment: followUpData.wentToAppointment,
            notes: followUpData.notes,
            scheduledAt: followUpData.scheduledAt,
          }
        );

        this.fetchFollowUps();
        this.closeAddFollowUpOverlay();
        this.closeOverlay();
        this.$emit("refreshPatientReferrals");
      } catch (error) {
        console.error("Error saving follow-up:", error);
        // TODO: Show user-friendly error message
      }
    },

    getStageFormatted(stage) {
      return PATIENT_NAVIGATION_STAGE_OPTIONS[stage] || stage || "N/A";
    },

    getReferralType(referral) {
      if (referral?.patientNavigationSetting?.name) {
        return referral.patientNavigationSetting.name;
      }

      const referralTypeId =
        referral?.patientNavigationSetting?.id ??
        referral?.referralType ??
        referral?.idPatientNavigationSetting ??
        null;

      if (referralTypeId != null) {
        const mappedValue = this.referralTypeMap[String(referralTypeId)];
        if (mappedValue) {
          return mappedValue;
        }
      }

      return "N/A";
    },

    async loadReferralTypeSettings() {
      try {
        const { data } = await fetchPatientNavigationSettings({
          params: {
            size: 200,
            filters: { disabled: false },
            order: { by: "patient-navigation-name", direction: "asc" },
          },
        });
        const settings = data?.patientNavigationSettings || [];
        const lookup = {};
        settings.forEach((setting) => {
          if (setting?.id && setting?.name) {
            lookup[String(setting.id)] = setting.name;
          }
        });
        this.referralTypeMap = lookup;
      } catch (error) {
        console.error(error);
        this.referralTypeMap = {};
      }
    },

    async fetchCommunityOrganization() {
      try {
        const res = await fetchCommunityCareOrganizationById(
          this.$route.params.idCommunity
        );
        this.communityOrganization = res.data;
      } catch (err) {
        console.error("Error fetching community organization:", err);
        this.communityOrganization = null;
      }
    },

    async fetchPatientReferralById() {
      this.fetchPatientReferralByIdStatus = PENDING;
      try {
        const res = await fetchPatientNavigationReferralById(
          this.showReferral.id,
          {
            params: {
              include: ["communityCare", "cancelled", "patient"],
            },
          }
        );
        this.showReferralDetails = res.data.patientNavigation;
        this.fetchPatientReferralByIdStatus = SUCCESS;
      } catch (err) {
        this.fetchPatientReferralByIdStatus = ERROR;
        console.error(err);
      }
    },

    async fetchFollowUps() {
      try {
        const res = await fetchPatientNavigationReferralFollowUps(
          this.showReferral.id
        );
        this.followUps = res.data.followUps || [];
      } catch (err) {
        console.error("Error fetching follow-ups:", err);
        this.followUps = [];
      }
    },

    getActionFormatted(action) {
      const actionMap = {
        "1st_follow_up": "1st Follow-up",
        "2nd_follow_up": "2nd Follow-up",
        additional_notes: "Additional Notes",
        complete_schedule_new: "Complete Schedule New",
        complete_referral: "Complete Referral",
      };
      return actionMap[action] || action || "N/A";
    },

    getFollowUpTypeFormatted(followUpType) {
      const typeMap = {
        email: "Email",
        text: "Text",
        phone_call: "Phone Call",
        letter: "Letter",
        in_person: "In Person",
        n_a: "N/A",
      };
      return typeMap[followUpType] || followUpType || "N/A";
    },

    formatYesNoNA(value) {
      if (value === "yes" || value === true) return "Yes";
      if (value === "no" || value === false) return "No";
      return "N/A";
    },
  },

  created() {
    if (this.showReferral === null || this.showReferral === undefined) return;
    this.loadReferralTypeSettings();
    // Skip community organization fetch when called from notifications
    if (!this.fromNotifications) {
      this.fetchCommunityOrganization();
    }
    this.fetchPatientReferralById();
    this.fetchFollowUps();
  },
});
</script>

<style scoped lang="scss">
.patient-section {
  position: relative;
  z-index: 1;
  margin-bottom: 25px;
  padding: 30px 24px 35px;
  background-color: $primaryBgBlue;
  color: $primaryLightBlue;
  &__top {
    margin-bottom: 8px;
    p {
      margin: 0;
      font-size: 16px;
      line-height: 1.67;
      color: #019bdf;

      &.patient-name {
        font-size: 21px;
        margin-bottom: 8px;
      }

      &.small-text {
        font-size: 16px;
        margin-bottom: 4px;
      }

      &.bold {
        font-weight: 700;
      }
    }
  }
}

.follow-up-warning {
  background-color: #f8e71c;
  margin: 0 0px 25px 0px;
  padding: 15px 20px;

  &__content {
    span {
      color: #000000;
      font-weight: 600;
      font-size: 16px;
      line-height: 1.5;
    }
  }
}
</style>
